import { useCallback, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { round } from 'es-toolkit';
import { format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, Dialog, Stack, Typography } from '@mui/material';

import { selectedDeviceSafeAtom } from '../common/MapView';
import { deviceDataAtom } from '@/interfaces/devices';
import { deviceDataPeriodAtom } from '@/components/devices/DeviceDetail';
import { tempUnitAtom, timeZoneAtom } from '@/interfaces/utils';
import { getTemperatureText } from '@/components/common/utils';
import DatePicker from '@/components/common/DatePicker';
import DeviceDetailGraph from './DeviceDetailGraph';
import LogTable from './LogTable';
import ExportDialog from '@/components/devices/ExportDialog';

import type { GraphData } from '@/@types/device';

export default function DeviceDetailReports() {
  const { isHumiditySupported } = useAtomValue(selectedDeviceSafeAtom);
  const { data, isPending } = useAtomValue(deviceDataAtom);
  const timeZone = useAtomValue(timeZoneAtom);
  const tempUnit = useAtomValue(tempUnitAtom);

  const [openDialog, setOpenDialog] = useState(false);
  const { t, i18n } = useTranslation();

  const timeFormat = i18n.language.startsWith('ko') ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';

  const tableHeader = useMemo(() => {
    const header = [t('Devices.label.time'), t('Devices.label.temperature')];
    if (isHumiditySupported) header.push(t('Devices.label.humidity'));
    return header;
  }, [t, isHumiditySupported]);

  const tempData = useMemo<GraphData[]>(() => {
    if (isPending) return [];
    return data.map((row) => ({
      x: row.time,
      y: row.temperature,
    }));
  }, [data, isPending]);
  const humidData = useMemo<GraphData[]>(() => {
    if (isPending || !isHumiditySupported) return [];
    return data.map((row) => ({
      x: row.time,
      y: row.humid,
    }));
  }, [data, isHumiditySupported, isPending]);
  const tableData = useMemo<(string | number)[][]>(() => {
    if (isPending) return [];
    return data.map((row) => {
      const r = [
        format(row.time, timeFormat, { in: tz(timeZone) }),
        getTemperatureText(row.temperature, tempUnit, true),
      ];
      if (row.humid) r.push(row.humid + '%');
      return r;
    });
  }, [data, isPending, tempUnit, timeFormat, timeZone]);
  const statData = useMemo<(string | number)[][]>(() => {
    let columns: (string | number)[][] = [
      [t('Devices.label.min')],
      [t('Devices.label.current')],
      [t('Devices.label.max')],
      ['MKT'],
    ];
    if (isPending || data.length === 0) return columns;

    const temperatures = data.map((row) => row.temperature);
    const tempColumns = [
      Math.min(...temperatures),
      data[0].temperature,
      Math.max(...temperatures),
      getMKT(temperatures),
    ].map((cell) => getTemperatureText(cell, tempUnit, true));
    columns = columns.map((col, i) => col.concat(tempColumns[i]));

    if (isHumiditySupported) {
      const humids = data.map((row) => row.humid);
      const humidColumns = [Math.min(...humids), data[0].humid, Math.max(...humids)];
      columns = columns.map((col, i) => col.concat(humidColumns[i]));
    }

    return columns;
  }, [data, isHumiditySupported, isPending, t, tempUnit]);

  const onDialogClose = useCallback(() => {
    setOpenDialog(false);
  }, []);

  return (
    <>
      <Stack gap={4} sx={{ flexGrow: 1, overflow: 'auto', pt: 1 }}>
        <DatePicker periodAtom={deviceDataPeriodAtom} showShortcut />
        {isPending ? (
          <Stack justifyContent="center" alignItems="center" sx={{ aspectRatio: 2, bgcolor: 'white' }}>
            <CircularProgress color="primary" />
          </Stack>
        ) : (
          <DeviceDetailGraph tempData={tempData} humidData={humidData} />
        )}

        <Stack
          direction="row"
          gap={4}
          justifyContent="space-around"
          sx={{ boxShadow: 1, borderRadius: '12px', py: 2, bgcolor: 'white' }}
        >
          {statData.map((data) => (
            <Stack key={data[0]} alignItems="center">
              <Typography color="primary">{data[0]}</Typography>
              <Typography variant="h5">{data[1] ?? '--'}</Typography>
              <Typography>{data[2] ? data[2] + '%' : ''}</Typography>
            </Stack>
          ))}
        </Stack>

        {!isPending && data.length > 0 && (
          <Button variant="contained" onClick={() => setOpenDialog(true)}>
            {t('Devices.label.export')}
          </Button>
        )}
        <LogTable header={tableHeader} data={tableData} />
      </Stack>

      <Dialog open={openDialog} onClose={onDialogClose}>
        <ExportDialog onClose={onDialogClose} />
      </Dialog>
    </>
  );
}

// Code from https://github.com/logtrack-io/service/blob/develop/packages/utils/src/temp-kit/calculateMkt.ts
function getMKT(data: number[]) {
  const numerator = -10000; // 분자 값. (-델타H/R) 일반적으로 10000으로 고정 (K)
  const n = data.length;

  // 켈빈온도로 변경 후 ln 진수의 분자 계산
  const logArgNumber = data.reduce((acc, t) => {
    const k = t + 273.15;
    acc += Math.exp(-10000 / k);

    return acc;
  }, 0);

  const denominator = Math.log(logArgNumber / n); // 분모

  let mkt = numerator / denominator - 273.15; // K -> °C
  mkt = round(mkt, 1); // 반올림 하여 소수 첫째 자리 까지 출력

  return mkt;
}
